import { conditionFromIndicator } from './conditions/conditionFromIndicator';
import { conditionFromWire } from './conditions/conditionFromWire';
import type { FlagConditionModel } from './conditions/FlagConditionModel';
import type { FormulaConditionModel } from './conditions/FormulaConditionModel';
import type { RangeConditionModel } from './conditions/RangeConditionModel';
import type { SetConditionModel } from './conditions/SetConditionModel';
import { SymbolListConditionModel } from './conditions/SymbolListConditionModel';
import type { SymbolListInstance } from './conditions/SymbolListConditionModel';
import { partitionRankConditions, RankStepsModel } from './RankStepsModel';
import type { RankDirection } from './RankStepsModel';
import { computed, createModel, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import { QueryObserver } from '@tanstack/react-query';
import type { QueryClient, QueryObserverResult } from '@tanstack/react-query';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
import type { ConcreteIndicator, Tag } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type {
    Condition,
    SelfAliasIndicatorFormula,
} from '@thinkalpha/platform-ws-client/contracts/ideas/conditions.js';
import type { Idea, ScreenerPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { getDictionaryTagsQuery, getIndicatorCompletionOptionsQuery } from 'src/queries/dictionary';
import type { FormulaService } from 'src/services/FormulaService';

export type UniverseConditionModel =
    | FlagConditionModel
    | RangeConditionModel
    | SetConditionModel
    | FormulaConditionModel;

export type UniverseConditionMode = Exclude<ScreenerPlan['mode'], 'include-any'>;

function coerceMode(mode: ScreenerPlan['mode']): UniverseConditionMode {
    return mode === 'include-any' ? 'include-all' : mode;
}

export type UniverseSearchModel = 'screener' | 'backtest';

export const ALL_TAG_VALUE = '__all__';

export interface UniverseTagOption {
    readonly tag: string;
    readonly label: string;
    readonly count: number;
}

export interface UniverseCreationInit {
    searchModel: UniverseSearchModel;
    queryClient: QueryClient;
    formulaService: FormulaService;
}

interface UniverseCreationState {
    readonly selectedConditions: ReadonlyArray<UniverseConditionModel>;
    readonly indicatorResponse: QueryObserverResult<ConcreteIndicator[], Error> | null;
    readonly dictionaryTagsResponse: QueryObserverResult<Tag[], Error> | null;
    readonly textFilter: string;
    readonly tagFilter: string | null;
    readonly mode: UniverseConditionMode;
    readonly includeSymbolListModel: SymbolListInstance;
    readonly excludeSymbolListModel: SymbolListInstance;
    readonly rankStepsModel: RankStepsModel;
    /**
     * Wire conditions queued by `hydrate` while the indicator catalog hasn't
     * loaded yet. Materialized + partitioned into `selectedConditions` and
     * `rankStepsModel` once the indicators query resolves.
     */
    readonly pendingConditions: ReadonlyArray<Condition> | null;
}

type UniverseCreationAction =
    | { type: 'SET_INDICATOR_RESPONSE'; result: QueryObserverResult<ConcreteIndicator[], Error> }
    | { type: 'SET_DICTIONARY_TAGS_RESPONSE'; result: QueryObserverResult<Tag[], Error> }
    | { type: 'SET_TEXT_FILTER'; value: string }
    | { type: 'SET_TAG_FILTER'; tag: string | null }
    | { type: 'SET_MODE'; mode: UniverseConditionMode }
    | { type: 'ADD_CONDITION'; condition: UniverseConditionModel }
    | { type: 'REMOVE_CONDITION'; id: string }
    | { type: 'CLEAR_CONDITIONS' }
    | { type: 'ADD_INCLUDE_SYMBOL'; symbol: string }
    | { type: 'ADD_EXCLUDE_SYMBOL'; symbol: string }
    | {
          type: 'HYDRATE';
          mode: UniverseConditionMode;
          includeList: string[];
          excludeList: string[];
          conditions: Condition[];
      }
    | { type: 'CLEAR' };

function findIndicatorBySlug(
    indicators: ReadonlyArray<ConcreteIndicator>,
    slug: string,
): ConcreteIndicator | undefined {
    return indicators.find((i) => i.matchSlug === slug);
}

function isDuplicate(existing: ReadonlyArray<UniverseConditionModel>, candidate: UniverseConditionModel): boolean {
    return existing.some((c) => c.id === candidate.id);
}

function filterIndicatorsBySearchModel(searchModel: UniverseSearchModel): string {
    switch (searchModel) {
        case 'screener':
            return 'screener eq true and hidden eq false';
        case 'backtest':
            return 'backtestUniverseSupported eq true and hidden eq false';
        default:
            throw new UnreachableCaseError(searchModel);
    }
}

function toWireCondition(model: UniverseConditionModel): Condition {
    const id = model.id;
    const enabled = model.enabled.value;
    switch (model.type) {
        case 'flag':
            return {
                type: 'membership',
                id,
                enabled,
                indicator: { id: model.indicator.value.id },
                invert: model.invert.value,
                selection: model.selection.value,
            };
        case 'range':
            return {
                type: 'quantitative',
                id,
                enabled,
                indicator: { id: model.indicator.value.id },
                min: model.min.value,
                max: model.max.value,
                selection: model.selection.value,
            };
        case 'set':
            return {
                type: 'equality',
                id,
                enabled,
                indicator: { id: model.indicator.value.id },
                values: [...model.values.value],
                selection: model.selection.value,
            };
        case 'formula':
            return {
                type: 'formula',
                id,
                enabled,
                imports: model.imports.value.map((imp) => imp.toRef().toContractType()),
                formula: model.formula.value,
            };
        default:
            throw new UnreachableCaseError(model);
    }
}

function materialize(
    wireConditions: ReadonlyArray<Condition>,
    indicators: ReadonlyArray<ConcreteIndicator>,
    searchModel: UniverseSearchModel,
    formulaService: FormulaService,
): UniverseConditionModel[] {
    const out: UniverseConditionModel[] = [];
    for (const wire of wireConditions) {
        const model = conditionFromWire(wire, indicators, searchModel, formulaService);
        if (model != null) out.push(model);
    }
    return out;
}

function disposeAll(conditions: ReadonlyArray<UniverseConditionModel>): void {
    for (const c of conditions) c[Symbol.dispose]();
}

function tryDrainPending(
    state: UniverseCreationState,
    searchModel: UniverseSearchModel,
    formulaService: FormulaService,
): UniverseCreationState {
    if (state.pendingConditions == null) return state;
    const indicators = state.indicatorResponse?.data ?? [];
    if (indicators.length === 0) return state;
    return applyConditions(state, state.pendingConditions, indicators, searchModel, formulaService);
}

function applyConditions(
    state: UniverseCreationState,
    conditions: ReadonlyArray<Condition>,
    indicators: ReadonlyArray<ConcreteIndicator>,
    searchModel: UniverseSearchModel,
    formulaService: FormulaService,
): UniverseCreationState {
    const topRankId = findIndicatorBySlug(indicators, Slugs.symbolTopRank)?.id;
    const bottomRankId = findIndicatorBySlug(indicators, Slugs.symbolBottomRank)?.id;
    const { rankSteps, others } = partitionRankConditions(conditions, topRankId, bottomRankId);
    state.rankStepsModel.setSteps(rankSteps);
    disposeAll(state.selectedConditions);
    return {
        ...state,
        selectedConditions: materialize(others, indicators, searchModel, formulaService),
        pendingConditions: null,
    };
}

function reducer(
    state: UniverseCreationState,
    action: UniverseCreationAction,
    searchModel: UniverseSearchModel,
    formulaService: FormulaService,
): UniverseCreationState {
    switch (action.type) {
        case 'SET_INDICATOR_RESPONSE': {
            if (state.indicatorResponse === action.result) return state;
            return tryDrainPending({ ...state, indicatorResponse: action.result }, searchModel, formulaService);
        }
        case 'SET_DICTIONARY_TAGS_RESPONSE': {
            if (state.dictionaryTagsResponse === action.result) return state;
            return { ...state, dictionaryTagsResponse: action.result };
        }
        case 'SET_TEXT_FILTER':
            if (state.textFilter === action.value) return state;
            return { ...state, textFilter: action.value };
        case 'SET_TAG_FILTER':
            if (state.tagFilter === action.tag) return state;
            return { ...state, tagFilter: action.tag };
        case 'SET_MODE':
            if (state.mode === action.mode) return state;
            return { ...state, mode: action.mode };
        case 'ADD_CONDITION':
            if (isDuplicate(state.selectedConditions, action.condition)) return state;
            return {
                ...state,
                selectedConditions: [...state.selectedConditions, action.condition],
            };
        case 'REMOVE_CONDITION': {
            const removed = state.selectedConditions.find((c) => c.id === action.id);
            if (removed == null) return state;
            removed[Symbol.dispose]();
            return { ...state, selectedConditions: state.selectedConditions.filter((c) => c.id !== action.id) };
        }
        case 'CLEAR_CONDITIONS':
            if (
                state.selectedConditions.length === 0 &&
                state.pendingConditions == null &&
                state.rankStepsModel.state.value.length === 0
            ) {
                return state;
            }
            state.rankStepsModel.clear();
            disposeAll(state.selectedConditions);
            return { ...state, selectedConditions: [], pendingConditions: null };
        case 'ADD_INCLUDE_SYMBOL':
            state.excludeSymbolListModel.removeSymbol(action.symbol);
            state.includeSymbolListModel.addSymbol(action.symbol);
            return state;
        case 'ADD_EXCLUDE_SYMBOL':
            state.includeSymbolListModel.removeSymbol(action.symbol);
            state.excludeSymbolListModel.addSymbol(action.symbol);
            return state;
        case 'HYDRATE': {
            state.includeSymbolListModel.setSymbols(action.includeList);
            state.excludeSymbolListModel.setSymbols(action.excludeList);
            const indicators = state.indicatorResponse?.data ?? [];
            if (indicators.length > 0) {
                return applyConditions(
                    { ...state, mode: action.mode },
                    action.conditions,
                    indicators,
                    searchModel,
                    formulaService,
                );
            }
            // Indicator catalog isn't ready yet — queue the full conditions
            // list; tryDrainPending fires on the next SET_INDICATOR_RESPONSE.
            state.rankStepsModel.clear();
            disposeAll(state.selectedConditions);
            return {
                ...state,
                mode: action.mode,
                selectedConditions: [],
                pendingConditions: action.conditions,
            };
        }
        case 'CLEAR':
            state.includeSymbolListModel.setSymbols([]);
            state.excludeSymbolListModel.setSymbols([]);
            state.rankStepsModel.clear();
            disposeAll(state.selectedConditions);
            return {
                ...state,
                mode: 'include-all',
                selectedConditions: [],
                pendingConditions: null,
                textFilter: '',
                tagFilter: null,
            };
        default:
            throw new UnreachableCaseError(action);
    }
}

/**
 * Form-level model for the universe-creation widget. Owns the editable
 * condition list, the indicator picker's text/tag filters, and the live
 * `QueryObserverResult` driving the available-indicator list.
 *
 * The model derives its indicator query from `searchModel` and owns the
 * `QueryObserver` it constructs against the caller's `queryClient`. A
 * single subscription effect forwards every result into the reducer via
 * `SET_INDICATOR_RESPONSE`; its cleanup unsubscribes the listener and
 * destroys the observer when the model is disposed (`createModel`
 * auto-disposes effects created during construction).
 */
export const UniverseCreationModel = createModel((init: UniverseCreationInit) => {
    const state = signal<UniverseCreationState>({
        selectedConditions: [],
        indicatorResponse: null,
        dictionaryTagsResponse: null,
        textFilter: '',
        tagFilter: null,
        mode: 'include-all',
        includeSymbolListModel: new SymbolListConditionModel({
            id: 'include-symbols',
            kind: 'include',
            symbols: [],
        }),
        excludeSymbolListModel: new SymbolListConditionModel({
            id: 'exclude-symbols',
            kind: 'exclude',
            symbols: [],
        }),
        rankStepsModel: new RankStepsModel(),
        pendingConditions: null,
    });

    const dispatch = (action: UniverseCreationAction) => {
        state.value = reducer(state.value, action, init.searchModel, init.formulaService);
    };

    // Dispose any conditions still held by the model when the model itself is
    // disposed. createModel auto-disposes effects registered during
    // construction, so this cleanup runs exactly once on UCM dispose.
    effect(() => () => {
        disposeAll(state.peek().selectedConditions);
    });

    const indicatorItemsObserver = new QueryObserver<ConcreteIndicator[], Error>(
        init.queryClient,
        getIndicatorCompletionOptionsQuery({
            resourceQuery: {
                filter: filterIndicatorsBySearchModel(init.searchModel),
                limit: 500,
            },
        }),
    );
    effect(() => {
        const unsubscribe = indicatorItemsObserver.subscribe((result) => {
            dispatch({ type: 'SET_INDICATOR_RESPONSE', result });
        });
        // Seed reducer state with the current observer snapshot.
        dispatch({ type: 'SET_INDICATOR_RESPONSE', result: indicatorItemsObserver.getCurrentResult() });
        return () => {
            unsubscribe();
            indicatorItemsObserver.destroy();
        };
    });

    const dictionaryTagsObserver = new QueryObserver<Tag[], Error>(init.queryClient, getDictionaryTagsQuery());
    effect(() => {
        const unsubscribe = dictionaryTagsObserver.subscribe((result) => {
            dispatch({ type: 'SET_DICTIONARY_TAGS_RESPONSE', result });
        });
        dispatch({ type: 'SET_DICTIONARY_TAGS_RESPONSE', result: dictionaryTagsObserver.getCurrentResult() });
        return () => {
            unsubscribe();
            dictionaryTagsObserver.destroy();
        };
    });

    const availableTagCounts: ReadonlySignal<Map<string, number>> = computed(() => {
        const tags = (state.value.dictionaryTagsResponse?.data ?? []).filter((t) => t.screener);
        const indicators = state.value.indicatorResponse?.data ?? [];
        const counts = new Map<string, number>();
        for (const tag of tags) {
            counts.set(tag.tag, indicators.filter((i) => i.tags.includes(tag.tag)).length);
        }
        return counts;
    });

    const topRankIndicator: ReadonlySignal<ConcreteIndicator | undefined> = computed(() =>
        findIndicatorBySlug(state.value.indicatorResponse?.data ?? [], Slugs.symbolTopRank),
    );
    const bottomRankIndicator: ReadonlySignal<ConcreteIndicator | undefined> = computed(() =>
        findIndicatorBySlug(state.value.indicatorResponse?.data ?? [], Slugs.symbolBottomRank),
    );

    const filteredIndicators: ReadonlySignal<ConcreteIndicator[]> = computed(() => {
        const indicators = state.value.indicatorResponse?.data ?? [];
        const text = state.value.textFilter;
        const tag = state.value.tagFilter;
        const topRankId = topRankIndicator.value?.id;
        const bottomRankId = bottomRankIndicator.value?.id;
        let filtered: ReadonlyArray<ConcreteIndicator> = indicators;
        if (text) {
            const needle = text.toLowerCase();
            filtered = indicators.filter(
                (i) => i.name.toLowerCase().includes(needle) || i.symbol.toLowerCase().includes(needle),
            );
        } else if (tag != null) {
            filtered = indicators.filter((i) => i.tags.includes(tag));
        }
        if (topRankId != null || bottomRankId != null) {
            filtered = filtered.filter((i) => i.id !== topRankId && i.id !== bottomRankId);
        }
        return filtered.toSorted((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    });

    const tagOptions: ReadonlySignal<UniverseTagOption[]> = computed(() => [
        {
            tag: ALL_TAG_VALUE,
            label: 'All Criteria',
            count: state.value.indicatorResponse?.data?.length ?? 0,
        },
        ...availableTagCounts.value.entries().map(([tag, count]) => ({ tag, label: tag, count })),
    ]);

    const errors: ReadonlySignal<string[]> = computed(() => [
        ...state.value.selectedConditions.flatMap((c) => c.validationErrors.value),
        ...state.value.includeSymbolListModel.validationErrors.value,
        ...state.value.excludeSymbolListModel.validationErrors.value,
        ...state.value.rankStepsModel.validationErrors.value,
    ]);
    const isValid: ReadonlySignal<boolean> = computed(() => errors.value.length === 0);

    const plan: ReadonlySignal<ScreenerPlan> = computed(() => {
        const rankConditions = state.value.rankStepsModel.toWireConditions(
            topRankIndicator.value?.id,
            bottomRankIndicator.value?.id,
        );
        return {
            type: 'screener',
            mode: state.value.mode,
            conditions: [...state.value.selectedConditions.map(toWireCondition), ...rankConditions],
            includeList: [...state.value.includeSymbolListModel.symbols.value],
            excludeList: [...state.value.excludeSymbolListModel.symbols.value],
        };
    });

    const idea: ReadonlySignal<Idea & { plan: ScreenerPlan }> = computed(() => ({
        isTemplate: false,
        name: null,
        key: 'ticker',
        description: null,
        plan: plan.value,
        defaultUniverseId: null,
        defaultColumnTemplateId: null,
    }));

    return {
        selectedConditions: computed(() => state.value.selectedConditions),
        indicatorResponse: computed(() => state.value.indicatorResponse),
        textFilter: computed(() => state.value.textFilter),
        tagFilter: computed(() => state.value.tagFilter),
        mode: computed(() => state.value.mode),
        includeSymbolListModel: computed(() => state.peek().includeSymbolListModel),
        excludeSymbolListModel: computed(() => state.peek().excludeSymbolListModel),
        rankSteps: computed(() => state.peek().rankStepsModel),
        topRankIndicator,
        bottomRankIndicator,
        availableTagCounts,
        filteredIndicators,
        tagOptions,
        errors,
        isValid,
        idea,
        addRankStep(direction: RankDirection) {
            state.peek().rankStepsModel.add(direction);
        },
        removeRankStep(id: string) {
            state.peek().rankStepsModel.remove(id);
        },
        setRankStepDirection(id: string, direction: RankDirection) {
            state.peek().rankStepsModel.setDirection(id, direction);
        },
        setRankStepCount(id: string, count: number | null) {
            state.peek().rankStepsModel.setCount(id, count);
        },
        setRankStepBy(id: string, bySelection: SelfAliasIndicatorFormula | null) {
            state.peek().rankStepsModel.setBy(id, bySelection);
        },
        moveRankStep(id: string, delta: -1 | 1) {
            state.peek().rankStepsModel.move(id, delta);
        },
        setTextFilter(value: string) {
            dispatch({ type: 'SET_TEXT_FILTER', value });
        },
        setTagFilter(tag: string | null) {
            dispatch({ type: 'SET_TAG_FILTER', tag });
        },
        setMode(mode: UniverseConditionMode) {
            dispatch({ type: 'SET_MODE', mode });
        },
        addCondition(condition: UniverseConditionModel) {
            dispatch({ type: 'ADD_CONDITION', condition });
        },
        addConditionForIndicator(indicator: ConcreteIndicator) {
            const condition = conditionFromIndicator(indicator, init.searchModel, init.formulaService);
            dispatch({ type: 'ADD_CONDITION', condition });
        },
        removeCondition(id: string) {
            dispatch({ type: 'REMOVE_CONDITION', id });
        },
        clearConditions() {
            dispatch({ type: 'CLEAR_CONDITIONS' });
        },
        addIncludeSymbol(symbol: string) {
            dispatch({ type: 'ADD_INCLUDE_SYMBOL', symbol });
        },
        addExcludeSymbol(symbol: string) {
            dispatch({ type: 'ADD_EXCLUDE_SYMBOL', symbol });
        },
        hydrate(params: {
            mode: ScreenerPlan['mode'];
            includeList: string[];
            excludeList: string[];
            conditions: Condition[];
        }) {
            const { mode, includeList, excludeList, conditions } = params;
            dispatch({
                type: 'HYDRATE',
                mode: coerceMode(mode),
                includeList,
                excludeList,
                conditions,
            });
        },
        clear() {
            dispatch({ type: 'CLEAR' });
        },
    };
});
